import type { AppRoute } from './types';
import { isPreloadable, type PreloadablePage } from './lazyPage';

/** Pages loaded per idle slot: small enough not to compete with what the user is doing. */
const BATCH = 4;

const whenIdle = (run: () => void): (() => void) => {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(run, { timeout: 2000 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(run, 200);
  return () => window.clearTimeout(id);
};

function pagesOf(routes: AppRoute[], into: Set<PreloadablePage>): Set<PreloadablePage> {
  for (const route of routes) {
    if (isPreloadable(route.component)) into.add(route.component);
    if (route.children) pagesOf(route.children, into);
  }
  return into;
}

/**
 * Loads the code of every page in `routes` in the background, a few at a time while the browser
 * is idle, so opening a page later fetches nothing. Pages already loaded cost nothing. Returns a
 * function that stops what has not started yet.
 */
export function preloadRoutes(routes: AppRoute[]): () => void {
  const queue = [...pagesOf(routes, new Set())];
  let cancelIdle: (() => void) | undefined;
  let stopped = false;

  const next = () => {
    if (stopped || queue.length === 0) return;
    cancelIdle = whenIdle(() => {
      // A page that fails here is fetched again when it is opened.
      void Promise.allSettled(queue.splice(0, BATCH).map((page) => page.preload())).then(next);
    });
  };
  next();

  return () => {
    stopped = true;
    cancelIdle?.();
  };
}
